import React, { useState, useEffect } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Cell 
} from 'recharts';
import { adminService } from '../services/adminApi';
import { Calendar, Music, AlertCircle } from 'lucide-react';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-4 border border-purple-100 shadow-xl rounded-xl max-w-xs z-50">
        <div className="flex items-center gap-2 mb-2 border-b border-gray-100 pb-2">
          <Music size={16} className="text-purple-600" />
          <p className="font-bold text-gray-900 text-sm leading-tight">{data.title}</p>
        </div>
        <p className="text-purple-600 font-bold mb-2 text-xs">Sung {data.count} times in last year</p>
        <div className="max-h-40 overflow-y-auto custom-scrollbar">
          <ul className="space-y-1">
            {data.dates.map((date, idx) => (
              <li key={idx} className="text-xs text-gray-600 flex items-center bg-gray-50 px-2 py-1 rounded">
                <Calendar size={12} className="mr-2 text-gray-400" />
                {new Date(date).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  return null;
};

const TopSongsChart = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await adminService.getTopSongsDetailed(5);
        setData(result);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch top songs", err);
        setError(err.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center bg-gray-50 rounded-2xl animate-pulse">
        <div className="text-gray-400 font-medium">Loading top songs...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-64 flex flex-col items-center justify-center bg-red-50 rounded-2xl border-2 border-dashed border-red-200">
        <AlertCircle className="text-red-400 mb-2" size={32} />
        <div className="text-red-500 font-medium">Error: {error}</div>
        <button 
          onClick={() => window.location.reload()}
          className="mt-2 text-xs text-red-600 underline hover:text-red-800"
        >
          Reload Page
        </button>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="h-64 flex flex-col items-center justify-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
        <Music className="text-gray-300 mb-2" size={32} />
        <div className="text-gray-400 font-medium">No songs recorded in the last year</div>
      </div>
    );
  }

  return (
    <div className="w-full h-72">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={data}
          margin={{
            top: 10,
            right: 30,
            left: 10,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
          <XAxis 
            type="number" 
            hide 
          />
          <YAxis 
            dataKey="title" 
            type="category" 
            width={150}
            tick={{ fontSize: 11, fill: '#4b5563', fontWeight: 600 }}
            tickFormatter={(value) => value.length > 20 ? `${value.substring(0, 20)}...` : value}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: '#faf5ff' }} />
          <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={32}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill="#9333ea" fillOpacity={0.8 - (index * 0.1)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TopSongsChart;
